import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Badge,
  useTheme,
  useMediaQuery,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  Storage as StorageIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
  Web as WebIcon,
  SupervisorAccount as SuperUserIcon,
  Build as BuildIcon,
  FolderOpen as ResourcesIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import apiClient from '../../services/apiClient';

const drawerWidth = 280;

const AdminLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [pendingAppCount, setPendingAppCount] = useState(null);

  useEffect(() => {
    apiClient.getApplications({ status: 'pending', limit: 1 })
      .then(data => setPendingAppCount(data.total ?? 0))
      .catch(() => setPendingAppCount(null));
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuExpand = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleLogout = () => {
    // Implement logout logic
    navigate('/login');
  };

  const menuItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/admin',
      exact: true
    },
    {
      id: 'applications',
      title: 'Applications',
      icon: <AssignmentIcon />,
      badge: pendingAppCount || null,
      children: [
        { title: 'All Applications', path: '/admin/applications' },
        { title: 'Pending Review', path: '/admin/applications/pending' },
        { title: 'Approved', path: '/admin/applications/approved' },
        { title: 'Rejected', path: '/admin/applications/rejected' }
      ]
    },
    {
      id: 'students',
      title: 'Students',
      icon: <SchoolIcon />,
      children: [
        { title: 'All Students', path: '/admin/students' },
        { title: 'Enrollment', path: '/admin/students/enrollment' },
        { title: 'Academic Records', path: '/admin/students/records' }
      ]
    },
    {
      id: 'users',
      title: 'User Management',
      icon: <PeopleIcon />,
      children: [
        { title: 'All Users', path: '/admin/users' },
        { title: 'Roles & Permissions', path: '/admin/users/roles' },
        { title: 'Staff Directory', path: '/admin/users/staff' }
      ]
    },
    {
      id: 'forms',
      title: 'Form Configuration',
      icon: <AssignmentIcon />,
      path: '/admin/form-config'
    },
    {
      id: 'communication',
      title: 'Communication',
      icon: <EmailIcon />,
      children: [
        { title: 'Email Templates', path: '/admin/communication/templates' },
        { title: 'Notifications', path: '/admin/communication/notifications' },
        { title: 'Announcements', path: '/admin/communication/announcements' }
      ]
    },
    {
      id: 'events',
      title: 'Events & Calendar',
      icon: <EventIcon />,
      children: [
        { title: 'All Events', path: '/admin/events' },
        { title: 'Calendar', path: '/admin/events/calendar' },
        { title: 'Event Categories', path: '/admin/events/categories' }
      ]
    },
    {
      id: 'finance',
      title: 'Financial Management',
      icon: <PaymentIcon />,
      children: [
        { title: 'Tuition & Fees', path: '/admin/finance/tuition' },
        { title: 'Payments', path: '/admin/finance/payments' },
        { title: 'Financial Reports', path: '/admin/finance/reports' }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Reports',
      icon: <AnalyticsIcon />,
      children: [
        { title: 'Dashboard Analytics', path: '/admin/analytics' },
        { title: 'Application Reports', path: '/admin/analytics/applications' },
        { title: 'Student Reports', path: '/admin/analytics/students' }
      ]
    },
    {
      id: 'system',
      title: 'System Management',
      icon: <SettingsIcon />,
      children: [
        { title: 'General Settings', path: '/admin/settings' },
        { title: 'Data Management', path: '/admin/data' },
        { title: 'System Monitoring', path: '/admin/monitoring' }
      ]
    },
    {
      id: 'security',
      title: 'Security & Access',
      icon: <SecurityIcon />,
      children: [
        { title: 'Security Settings', path: '/admin/security' },
        { title: 'Access Logs', path: '/admin/security/logs' },
        { title: 'Backup & Recovery', path: '/admin/security/backup' }
      ]
    },
    {
      id: 'resources',
      title: 'Resources & Downloads',
      icon: <ResourcesIcon />,
      path: '/admin/resources',
    },
    {
      id: 'website',
      title: 'Website Management',
      icon: <WebIcon />,
      children: [
        { title: 'Pages & Content', path: '/admin/website/pages' },
        { title: 'Announcements', path: '/admin/website/announcements' },
        { title: 'Navigation & Menus', path: '/admin/website/navigation' },
        { title: 'Media Library', path: '/admin/website/media' }
      ]
    },
    {
      id: 'superuser',
      title: 'Super User Console',
      icon: <SuperUserIcon />,
      children: [
        { title: 'Data Management', path: '/admin/superuser/data' },
        { title: 'Bulk Operations', path: '/admin/superuser/bulk' },
        { title: 'Audit Logs', path: '/admin/superuser/audit' },
        { title: 'System Config', path: '/admin/superuser/config' }
      ]
    }
  ];

  const isActiveRoute = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const renderMenuItem = (item) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus[item.id];
    const isActive = item.path ? isActiveRoute(item.path, item.exact) : false;

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                handleMenuExpand(item.id);
              } else if (item.path) {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }
            }}
            selected={isActive}
            sx={{
              minHeight: 48,
              px: 2.5,
              backgroundColor: isActive ? 'action.selected' : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 2,
                justifyContent: 'center',
                color: isActive ? 'primary.main' : 'inherit'
              }}
            >
              {item.badge ? (
                <Badge
                  badgeContent={item.badge}
                  color={item.badgeColor || 'error'}
                  max={99}
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.title}
              sx={{
                color: isActive ? 'primary.main' : 'inherit',
                '& .MuiListItemText-primary': {
                  fontWeight: isActive ? 600 : 400
                }
              }}
            />
            {hasChildren && (
              isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigate(child.path);
                      if (isMobile) {
                        setMobileOpen(false);
                      }
                    }}
                    selected={isActiveRoute(child.path)}
                    sx={{
                      pl: 6,
                      minHeight: 40,
                      backgroundColor: isActiveRoute(child.path) ? 'action.selected' : 'transparent'
                    }}
                  >
                    <ListItemText
                      primary={child.title}
                      sx={{
                        color: isActiveRoute(child.path) ? 'primary.main' : 'inherit',
                        '& .MuiListItemText-primary': {
                          fontSize: '0.875rem',
                          fontWeight: isActiveRoute(child.path) ? 600 : 400
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
          Eagles Refuge Academy
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Admin Panel
        </Typography>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ pt: 1 }}>
          {/* Back to Main Site */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate('/')}
              sx={{ minHeight: 48, px: 2.5 }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Back to Main Site" />
            </ListItemButton>
          </ListItem>

          <Divider sx={{ my: 1 }} />

          {menuItems.map(renderMenuItem)}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          Admin Panel v1.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Eagles Refuge Academy — Admin
          </Typography>

          {/* Notifications */}
          <IconButton color="inherit" sx={{ mr: 1 }}>
            <Badge badgeContent={pendingAppCount || null} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* Profile Menu */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="profile-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <AccountCircleIcon />
          </IconButton>

          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Admin User</Typography>
              <Typography variant="caption" color="text.secondary">admin@era.edu</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" label="Super User" sx={{ bgcolor: '#4a148c', color: 'white', fontSize: '0.7rem', height: 18 }} />
              </Box>
            </Box>
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              My Profile
            </MenuItem>
            <MenuItem onClick={handleProfileMenuClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Account Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px', // AppBar height
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
